/**
 * 💰 BOOKING COST PREVIEW COMPONENT
 * Displays real-time cost calculation with elite tier discounts
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { AttachMoney, Discount, Star } from '@mui/icons-material';
import { CostCalculation, CostBreakdownItem } from '@/services/adminBookingService';

interface BookingCostPreviewProps {
  calculation: CostCalculation | null;
  loading?: boolean;
  error?: string | null;
}

export const BookingCostPreview: React.FC<BookingCostPreviewProps> = ({
  calculation,
  loading = false,
  error = null,
}) => {
  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="70%" />
            <Divider />
            <Skeleton variant="text" width="50%" height={40} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" icon={<AttachMoney />}>
        {error}
      </Alert>
    );
  }

  if (!calculation || calculation.totalCost === undefined) {
    return (
      <Alert severity="info" icon={<AttachMoney />}>
        Fill in the booking details to see cost preview
      </Alert>
    );
  }

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) {
      return '₦0.00';
    }
    return `₦${amount.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getItemTypeColor = (type: CostBreakdownItem['type']) => {
    switch (type) {
      case 'rental':
        return 'primary';
      case 'fee':
        return 'default';
      case 'deposit':
        return 'warning';
      case 'addon':
        return 'info';
      default:
        return 'default';
    }
  };

  const getItemTypeLabel = (type: CostBreakdownItem['type']) => {
    switch (type) {
      case 'rental':
        return 'Rental';
      case 'fee':
        return 'Fee';
      case 'deposit':
        return 'Deposit';
      case 'addon':
        return 'Add-on';
      default:
        return type;
    }
  };

  const hasEliteDiscount =
    calculation.discounts?.eliteTierDiscount?.applied &&
    calculation.discounts.eliteTierDiscount.amount > 0;

  return (
    <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
      <CardContent>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney /> Cost Preview
          </Typography>
          {hasEliteDiscount && (
            <Chip
              icon={<Star />}
              label={`${calculation.discounts!.eliteTierDiscount!.tier} Member`}
              color="warning"
              size="small"
            />
          )}
        </Stack>

        {/* Elite Tier Discount Banner */}
        {hasEliteDiscount && (
          <Alert
            severity="success"
            icon={<Discount />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight={600}>
              Elite Member Discount Applied!
            </Typography>
            <Typography variant="caption">
              {calculation.discounts!.eliteTierDiscount!.tier} tier saves{' '}
              {formatCurrency(calculation.discounts!.eliteTierDiscount!.amount)}
            </Typography>
          </Alert>
        )}

        {/* Cost Breakdown */}
        <Stack spacing={1.5}>
          {(calculation.costBreakdown || []).map((item, index) => (
            <Box key={index}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">
                      {item.label}
                    </Typography>
                    <Chip
                      label={getItemTypeLabel(item.type)}
                      size="small"
                      color={getItemTypeColor(item.type)}
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                    {item.refundable && (
                      <Chip
                        label="Refundable"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                  </Stack>
                  {item.discount && item.originalAmount && (
                    <Typography variant="caption" color="success.main">
                      Saved {formatCurrency(item.discount)} (was {formatCurrency(item.originalAmount)})
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={item.type === 'rental' ? 600 : 400}
                  sx={{
                    textDecoration: item.discount ? 'line-through' : 'none',
                    color: item.discount ? 'text.secondary' : 'text.primary',
                  }}
                >
                  {formatCurrency(item.originalAmount || item.amount)}
                </Typography>
                {item.discount && (
                  <Typography variant="body2" fontWeight={600} color="success.main" sx={{ ml: 1 }}>
                    {formatCurrency(item.amount)}
                  </Typography>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Total */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            Total Amount
          </Typography>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            {formatCurrency(calculation.totalCost)}
          </Typography>
        </Stack>

        {/* Deposit Info */}
        {(calculation.costBreakdown || []).some(item => item.type === 'deposit' && item.refundable) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              Security deposit will be refunded after trip completion
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default BookingCostPreview;
